import React, { useEffect, useState, useMemo, useRef } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import { printTableElement } from '../../utils/exportUtils';
import { scopeAPI } from '../../services/authService';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab } from '../../utils/attachmentStorage';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';
import { formatDateIN } from '../../utils/dateFormat';

const ProjectHandOver = () => {
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [projectHandovers, setProjectHandovers] = useState([]);
  const [newItem, setNewItem] = useState({
    project_id: '',
    category: 'Land',
    item: '',
    handover_date: '',
    received_by: '',
    status: 'Pending',
    attachments: [],
    remarks: '',
  });
  const showProjectColumn = projectFilter === 'all';
  const tableRef = useRef(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);

  useEffect(() => {
    const loadHandovers = async () => {
      try {
        const response = await scopeAPI.getProjectHandovers();
        const { rows } = unwrapListResponse(response);
        const mapped = safeArray(rows).map((row) => ({
          id: row.id,
          project_id: row.projectId ?? row.project_id ?? 1,
          item: row.item || '',
          category: row.category || 'General',
          handover_date: row.handoverDate ? String(row.handoverDate).slice(0, 10) : (row.handover_date || null),
          received_by: row.receivedBy ?? row.received_by ?? null,
          status: row.status || 'Pending',
          attachments: Array.isArray(row.attachments) ? row.attachments : [],
          remarks: row.remarks || '',
        }));
        setProjectHandovers(mapped);
      } catch (error) {
        console.warn('Project handover API unavailable, using local data:', error?.message || error);
        setProjectHandovers([]);
      } finally {
        setDataReady(true);
      }
    };
    loadHandovers();
  }, []);

  const projects = safeArray(projectOptions);

  const filteredItems = useMemo(() => {
    let items = [...safeArray(projectHandovers)];
    if (projectFilter !== 'all') items = items.filter(i => i.project_id === parseInt(projectFilter));
    if (statusFilter !== 'all') items = items.filter(i => i.status === statusFilter);
    if (categoryFilter !== 'all') items = items.filter(i => i.category === categoryFilter);
    return items;
  }, [projectHandovers, projectFilter, statusFilter, categoryFilter]);

  const categories = [...new Set(safeArray(projectHandovers).map(h => h?.category).filter(Boolean))];
  const completedCount = filteredItems.filter(i => i?.status === 'Completed').length;
  const pendingCount = filteredItems.filter(i => i?.status === 'Pending').length;
  const statusOptions = ['Pending', 'In Progress', 'Completed', 'On Hold'];

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>Project Hand Over</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setNewItem((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  const handleRemoveAttachment = (index) => {
    setNewItem((prev) => {
      const next = [...(prev.attachments || [])];
      next.splice(index, 1);
      return { ...prev, attachments: next };
    });
  };

  const handleUpdateHandoverStatus = async (item, nextStatus) => {
    if (!(await confirmDialog(`Update status to "${nextStatus}"?`, 'Update Handover Status'))) return;
    try {
      const payload = {
        projectId: Number(item.project_id),
        item: item.item,
        category: item.category,
        handoverDate: item.handover_date ? new Date(item.handover_date).toISOString() : null,
        receivedBy: item.received_by || null,
        status: nextStatus,
        attachments: item.attachments || [],
        remarks: item.remarks || null,
      };
      const response = await scopeAPI.updateProjectHandover(item.id, payload);
      const row = response?.data || {};
      setProjectHandovers((prev) => prev.map((handover) => (
        handover.id === item.id
          ? {
              ...handover,
              status: row.status || nextStatus,
            }
          : handover
      )));
      notifySuccess('Handover status updated.');
    } catch (error) {
      console.error('Update handover status failed via API:', error);
      notifyError('Unable to update handover status. Please retry.');
    }
  };
  const handleCreateHandover = async () => {
    if (!newItem.project_id || !newItem.item.trim()) {
      notifyError('Project and item description are required.');
      return;
    }
    if (!(await confirmDialog('Add this handover item?', 'New Handover Item'))) return;
    try {
      const payload = {
        projectId: Number(newItem.project_id),
        item: newItem.item.trim(),
        category: newItem.category,
        handoverDate: newItem.handover_date ? new Date(newItem.handover_date).toISOString() : null,
        receivedBy: newItem.received_by.trim() || null,
        status: newItem.status,
        attachments: newItem.attachments || [],
        remarks: newItem.remarks.trim() || null,
      };
      const response = await scopeAPI.createProjectHandover(payload);
      const row = response?.data;
      setProjectHandovers((prev) => ([
        {
          id: row.id,
          project_id: row.projectId ?? Number(newItem.project_id),
          item: row.item,
          category: row.category,
          handover_date: row.handoverDate ? String(row.handoverDate).slice(0, 10) : newItem.handover_date || null,
          received_by: row.receivedBy ?? newItem.received_by ?? null,
          status: row.status || newItem.status,
          attachments: Array.isArray(row.attachments) ? row.attachments : (newItem.attachments || []),
          remarks: row.remarks || newItem.remarks,
        },
        ...prev,
      ]));
      setShowModal(false);
      setNewItem({
        project_id: '',
        category: 'Land',
        item: '',
        handover_date: '',
        received_by: '',
        status: 'Pending',
        attachments: [],
        remarks: '',
      });
      notifySuccess('Handover item added successfully.');
    } catch (error) {
      console.error('Create project handover failed via API:', error);
      notifyError('Unable to add handover item. Please retry.');
    }
  };

  return (
    <div>
      <div className="section-header">
        <h2>Project Hand Over <span className="count-badge">{filteredItems.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'Project Hand Over - Filtered', subtitle: `Project: ${projectFilter === 'all' ? 'All Projects' : (projects.find(p => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project')}`, element: tableRef.current })}
          >
            Download PDF
          </button>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Handover Item
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#d1fae5' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Completed</p>
            <p className="stat-value">{completedCount}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef3c7' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Pending</p>
            <p className="stat-value">{pendingCount}</p>
          </div>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={projectFilter}
          onChange={setProjectFilter}
          projects={projects}
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
          <option value="all">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          {statusOptions.map((status) => (
            <option key={status} value={status}>{status}</option>
          ))}
        </select>
        <ResetFiltersButton
          onClick={() => {
            setProjectFilter('all');
            setCategoryFilter('all');
            setStatusFilter('all');
          }}
        />
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Item</th>
              {showProjectColumn && <th>Project</th>}
              <th>Category</th>
              <th>Handover Date</th>
              <th>Received By</th>
              <th>Status</th>
              <th>Attachments</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => {
              const project = projects.find(p => p.id === item.project_id);
              return (
                <tr key={item.id} className={item.status === 'Pending' ? '' : 'muted'}>
                  <td style={{ fontWeight: 500, maxWidth: 280 }}>{item.item}</td>
                  {showProjectColumn && <td style={{ fontSize: 12, color: '#6b7280' }}>{project?.name}</td>}
                  <td><span className={`status-badge ${item.category === 'Land' ? 'active' : item.category === 'Drawings' ? 'open' : item.category === 'Reports' ? 'medium' : 'high'}`}>{item.category}</span></td>
                  <td>{formatDateIN(item.handover_date)}</td>
                  <td style={{ fontSize: 13 }}>{item.received_by || '-'}</td>
                  <td>
                    <select
                      value={item.status}
                      onChange={(e) => handleUpdateHandoverStatus(item, e.target.value)}
                    >
                      {statusOptions.map((status) => (
                        <option key={`${item.id}-${status}`} value={status}>{status}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ minWidth: 210 }}>
                    {item.attachments?.length ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {item.attachments.map((file, index) => (
                          <div
                            key={`${item.id}-${file.name}-${index}`}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
                          >
                            <span style={{ fontSize: 12, color: '#4b5563', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {file.name}
                            </span>
                            <span style={{ display: 'flex', gap: 4 }}>
                              <button className="btn-secondary btn-sm" onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, `Handover attachment metadata: ${file.name}`))}>View</button>
                              <button className="btn-secondary btn-sm" onClick={() => downloadAttachment(file, `Handover attachment metadata: ${file.name}`)}>Download</button>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>No files</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: '#6b7280', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.remarks}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add Handover Item</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group"><label>Project <span className="req">*</span></label><select value={newItem.project_id} onChange={(e) => setNewItem({ ...newItem, project_id: e.target.value })}><option value="">Select Project</option>{projects.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
                <div className="form-group"><label>Category</label><select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}><option value="Land">Land</option><option value="Drawings">Drawings</option><option value="Reports">Reports</option><option value="Approvals">Approvals</option><option value="Equipment">Equipment</option></select></div>
              </div>
              <div className="form-group"><label>Item Description <span className="req">*</span></label><input type="text" value={newItem.item} onChange={(e) => setNewItem({ ...newItem, item: e.target.value })} placeholder="Describe the handover item" /></div>
              <div className="form-row">
                <div className="form-group"><label>Handover Date</label><input type="date" value={newItem.handover_date} onChange={(e) => setNewItem({ ...newItem, handover_date: e.target.value })} /></div>
                <div className="form-group"><label>Received By</label><input type="text" value={newItem.received_by} onChange={(e) => setNewItem({ ...newItem, received_by: e.target.value })} placeholder="Name of person receiving" /></div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select value={newItem.status} onChange={(e) => setNewItem({ ...newItem, status: e.target.value })}>
                    {statusOptions.map((status) => (
                      <option key={`new-${status}`} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Attachments</label>
                <input
                  type="file"
                  multiple
                  onChange={handleAttachmentChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  disabled={attachmentUploading}
                />
                {attachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {newItem.attachments.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {newItem.attachments.map((file, idx) => (
                      <div
                        key={`${file.name}-${file.size}-${idx}`}
                        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: '#374151', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '6px 8px' }}
                      >
                        <span>{file.name}</span>
                        <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={{ color: '#22c55e' }}>✓</span>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, 'Attachment uploaded from Project Handover.'))}>View</button>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => downloadAttachment(file, 'Attachment uploaded from Project Handover.')}>Download</button>
                          <button type="button" className="btn-secondary btn-sm" onClick={() => handleRemoveAttachment(idx)} title="Remove">✕</button>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group"><label>Remarks</label><textarea value={newItem.remarks} onChange={(e) => setNewItem({ ...newItem, remarks: e.target.value })} placeholder="Additional remarks..." rows={2} /></div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleCreateHandover} disabled={attachmentUploading}>Add Item</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectHandOver;
